import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GoogleMaps } from '../../../../components';
import {
  ANOMALY_SVG_PATH,
  ANOMALY_TYPE,
  DMS_TYPE,
  SOS_STATUS_KEYS,
  SOS_TICKET_STATUS_BG_COLOR,
  ANOMALY_BG_COLOR,
} from '../../../../utilities/constants';
import {
  rashDrivingMarker,
  sosAlertMarker,
  driverDistractionMarker,
  driverDrowsinessMarker,
  doorOpenMarker,
  overspeedMarker,
} from '../../../../utilities/markersData';
import anomalyVector from '../../../../assets/anomaly-vector.svg';
import AddressFetcher from '../../../VehicleMonitoring/components/AddressFetcher';
import { defaultCenter } from '../../../../utilities/dummyData';
import { createMarker } from '../../../../utilities/commonFunctions';

const SafetyAnomalyMaps = ({
  sosData,
  anomalyData,
  selectedTabKey,
  activeKey,
  isFirstCall = false,
}) => {
  const [markers, setMarkers] = useState([]);
  const [center, setCenter] = useState(defaultCenter);
  const [infoWindow, setInfoWindow] = useState({});
  const [tripIdList, setTripIdList] = useState([]);
  const [sosFilteredData, setSosFilteredData] = useState({});
  const [anomalyFilteredData, setAnomalyFilteredData] = useState({});
  const hasUserInteractedRef = useRef(false);

  const filterValidLatLng = data =>
    Object.fromEntries(
      Object.entries(data || {}).map(([key, items]) => [
        key,
        items.filter(item => item?.latitude && item?.longitude),
      ])
    );

  useEffect(() => {
    setSosFilteredData(filterValidLatLng(sosData));
    setAnomalyFilteredData(filterValidLatLng(anomalyData));
  }, [sosData, anomalyData]);

  const getUniqueTripIds = () => {
    const source = selectedTabKey === 0 ? sosFilteredData : anomalyFilteredData;
    return [
      ...new Set(
        Object.values(source || {})
          .flat()
          .map(item => item?.trip_id)
          .filter(Boolean)
      ),
    ];
  };

  useEffect(() => {
    setTripIdList(getUniqueTripIds());
  }, [selectedTabKey, sosFilteredData, anomalyFilteredData]);

  const generateMarkers = (type, title, data, color) => {
    if (!data) return [];

    const validElement = data.find(el => el?.latitude && el?.longitude);
    if (validElement && !hasUserInteractedRef.current) {
      setCenter({
        lat: Number(validElement.latitude),
        lng: Number(validElement.longitude),
      });
    }

    return data.map((element, index) => {
      const iconMarker = getMarkerIcon(
        type?.includes('sos') ? type : element?.type
      );

      return {
        ...iconMarker,
        key: `${element?.type || type}-marker-${index}`,
        position: {
          lat: Number(element.latitude),
          lng: Number(element.longitude),
        },
        onMarkerClick: () =>
          handleMarkerClick(
            `info-window-${element?.trip_id}`,
            { lat: Number(element.latitude), lng: Number(element.longitude) },
            {
              Trip_ID: element?.trip_id,
              Time: element?.timestamp,
              Vehicle_Number: element?.vehicle_number,
            },
            {
              text: element?.type ? getAnomalyTitle(element?.type) : title,
              bgColor: color,
            },
            <AddressFetcher
              type="map"
              location={{
                latitude: element.latitude,
                longitude: element.longitude,
              }}
              fontSize="0.75rem"
              title={
                element?.pickup_drop_type
                  ? `${element?.pickup_drop_type} location`
                  : 'Location'
              }
            />
          ),
      };
    });
  };

  const applyMarkerData = (sourceData, trip_id = '') => {
    const filteredData = {};
    Object.keys(sourceData).forEach(key => {
      filteredData[key] = trip_id
        ? sourceData[key].filter(item => item?.trip_id === trip_id)
        : sourceData[key];
    });
    return filteredData;
  };

  const loadMarkers = trip_id => {
    setMarkers([]);

    if (selectedTabKey === 0) {
      const data = applyMarkerData(sosFilteredData, trip_id);
      setMarkers([
        ...generateMarkers(
          'sos-new',
          'New',
          data[SOS_STATUS_KEYS.PENDING],
          SOS_TICKET_STATUS_BG_COLOR.NEW
        ),
        ...generateMarkers(
          'sos-inprogress',
          'In Progress',
          data[SOS_STATUS_KEYS.IN_PROGRESS],
          SOS_TICKET_STATUS_BG_COLOR.INPROGRESS
        ),
        ...generateMarkers(
          'sos-resolved',
          'Resolved',
          data[SOS_STATUS_KEYS.RESOLVED],
          SOS_TICKET_STATUS_BG_COLOR.RESOLVED
        ),
      ]);
    }

    if (selectedTabKey === 1) {
      const data = applyMarkerData(anomalyFilteredData, trip_id);
      setMarkers([
        ...generateMarkers(
          'anomaly',
          'Critical Anomaly',
          data[ANOMALY_TYPE.DRIVER_DROWSINESS],
          ANOMALY_BG_COLOR.CRITICAL
        ),
        ...generateMarkers(
          'anomaly',
          'Major Anomaly',
          data[ANOMALY_TYPE.MAJOR],
          ANOMALY_BG_COLOR.MAJOR
        ),
        ...generateMarkers(
          'anomaly',
          'Minor Anomaly',
          data[ANOMALY_TYPE.MINOR],
          ANOMALY_BG_COLOR.MINOR
        ),
      ]);
    }
  };

  useEffect(() => {
    loadMarkers();
  }, [selectedTabKey, sosFilteredData, anomalyFilteredData, activeKey]);

  const handleMarkerClick = (key, position, content, title, children) => {
    setInfoWindow({ title, content, position, children, key, isVisible: true });
  };

  const getMarkerIcon = type => {
    switch (type) {
      case 'sos-new':
        return {
          ...sosAlertMarker,
          name: type,
          icon: {
            ...sosAlertMarker.icon,
            url: createMarker({
              backgroundColor: SOS_TICKET_STATUS_BG_COLOR.NEW,
              iconPath: ANOMALY_SVG_PATH.SOS,
            }),
          },
          hoveredBackgroundColor: SOS_TICKET_STATUS_BG_COLOR.NEW_HOVER,
        };
      case 'sos-inprogress':
        return {
          ...sosAlertMarker,
          name: type,
          icon: {
            ...sosAlertMarker.icon,
            url: createMarker({
              backgroundColor: SOS_TICKET_STATUS_BG_COLOR.INPROGRESS,
              iconPath: ANOMALY_SVG_PATH.SOS,
            }),
          },
          hoveredBackgroundColor: SOS_TICKET_STATUS_BG_COLOR.INPROGRESS_HOVER,
        };
      case 'sos-resolved':
        return {
          ...sosAlertMarker,
          name: type,
          icon: {
            ...sosAlertMarker.icon,
            url: createMarker({
              backgroundColor: SOS_TICKET_STATUS_BG_COLOR.RESOLVED,
              iconPath: ANOMALY_SVG_PATH.SOS,
            }),
          },
          hoveredBackgroundColor: SOS_TICKET_STATUS_BG_COLOR.RESOLVED_HOVER,
        };
      case 'rash_driving':
        return rashDrivingMarker;
      case DMS_TYPE.DROWSINESS:
        return driverDrowsinessMarker;
      case DMS_TYPE.DISTRACTION:
        return driverDistractionMarker;
      case 'door_open':
        return doorOpenMarker;
      case 'overspeed':
        return overspeedMarker;
      default:
        return anomalyVector;
    }
  };

  const getAnomalyTitle = type => {
    switch (type) {
      case 'rash_driving':
        return 'Rash Driving';
      case DMS_TYPE.DROWSINESS:
        return 'Driver Drowsiness';
      case DMS_TYPE.DISTRACTION:
        return 'Driver Distraction';
      case 'door_open':
        return 'Door Open';
      case 'overspeed':
        return 'Over Speeding';
      default:
        return '';
    }
  };

  const onSelectChange = trip_id => {
    loadMarkers(trip_id);
  };

  return (
    <GoogleMaps
      markers={markers}
      center={center}
      infoWindow={infoWindow}
      closeInfoWindow={() => setInfoWindow({ isVisible: false })}
      showLegend
      isMapLegendVertical
      showFilter
      mapFilterProps={{
        mapFilterList: tripIdList,
        title: 'Trip ID',
        onChange: onSelectChange,
      }}
      showMarkerHoverEffectOnHovered
      preserveViewportOnUserInteraction={!isFirstCall}
      onUserInteraction={() => {
        hasUserInteractedRef.current = true;
      }}
      enableRecenter
    />
  );
};

SafetyAnomalyMaps.propTypes = {
  sosData: PropTypes.object,
  anomalyData: PropTypes.object,
  selectedTabKey: PropTypes.number,
  activeKey: PropTypes.string,
  isFirstCall: PropTypes.bool,
};

export default React.memo(SafetyAnomalyMaps);
